// src/features/hotels/components/config/HotelTexts.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import {
    Box,
    Typography,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Paper,
    Grid,
    IconButton,
    ToggleButtonGroup,
    ToggleButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    Divider,
    Chip,
    InputAdornment,
    Tooltip,
    Stack,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Language as LanguageIcon,
    Save as SaveIcon,
    TextFields as TextFieldsIcon,
    Palette as PaletteIcon,
    CloudUpload as UploadIcon,
    Warning as WarningIcon,
    Translate as TranslateIcon,
    ColorLens as ColorIcon,
    Image as ImageIcon,
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

// COMPONENTES DEL SISTEMA
import { MyFormContainer } from '../../../../components/forms/MyFormContainer';
import { MyFormSection } from '../../../../components/forms/MyFormLayout';
import { useHotelPermissions } from '../../hooks/useHotelPermissions';
import { Hotel, HOTEL_LANGUAGES } from '../../types';

// Componente mejorado para selector de color
const ColorPicker: React.FC<{
    value: string;
    onChange: (color: string) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box >
                    <TextField
                        type="color"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={disabled}
                        sx={{
                            width: 60,
                            height: 60,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            '& input[type="color"]': {
                                border: 'none',
                                borderRadius: 1,
                                width: '100%',
                                height: '100%',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                            },
                            '& .MuiOutlinedInput-root': {
                                padding: 0,
                            }
                        }}
                    />
                </Box>
                <TextField
                    value={value.toUpperCase()}
                    onChange={(e) => {
                        const hexColor = e.target.value;
                        if (/^#[0-9A-F]{0,6}$/i.test(hexColor)) {
                            onChange(hexColor);
                        }
                    }}
                    disabled={disabled}
                    placeholder="#000000"
                    label="Código HEX"
                    size="small"
                    sx={{ width: 140 }}
                    InputProps={{
                        style: { fontFamily: 'monospace' }
                    }}
                />
                
            </Box>
        </Stack>
    );
};

const HotelTexts: React.FC = () => {
    // Hooks y estados iniciales
    const { id: hotelId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dataProvider = useDataProvider() as any;
    const notify = useNotify();
    const translate = useTranslate();

    // Estados principales
    const [loading, setLoading] = useState(true);
    const [hotel, setHotel] = useState<Hotel | null>(null);
    const permissions = useHotelPermissions(hotel);

    // === SECCIÓN 1: TEXTOS PERSONALIZADOS ===
    const [savingTexts, setSavingTexts] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('ES');
    const [availableLanguages, setAvailableLanguages] = useState<string[]>(['ES']);
    const [customTexts, setCustomTexts] = useState<{ key: string; value: string }[]>([]);
    const [originalTexts, setOriginalTexts] = useState<{ key: string; value: string }[]>([]);
    const [newText, setNewText] = useState({ key: '', value: '' });
    const [addTextOpen, setAddTextOpen] = useState(false);
    const [addLanguageOpen, setAddLanguageOpen] = useState(false);
    const [newLanguageCode, setNewLanguageCode] = useState('');
    const [searchText, setSearchText] = useState('');

    // === SECCIÓN 2: BRANDING (COLOR Y LOGO) ===
    const [savingBranding, setSavingBranding] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#F85100');
    const [originalColor, setOriginalColor] = useState('#F85100');
    const [logoBase64, setLogoBase64] = useState<string | null>(null);
    const [originalLogoBase64, setOriginalLogoBase64] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);

    // === DIÁLOGOS DE CONFIRMACIÓN ===
    const [exitDialogOpen, setExitDialogOpen] = useState(false);
    const [changeLanguageDialogOpen, setChangeLanguageDialogOpen] = useState(false);
    const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);

    const [checkingDefaultTexts, setCheckingDefaultTexts] = useState(false);

    // Detectar cambios no guardados
    const unsavedTextChanges = useMemo(() => 
        JSON.stringify(customTexts) !== JSON.stringify(originalTexts), 
        [customTexts, originalTexts]
    );

    const unsavedBrandingChanges = useMemo(() => 
        primaryColor !== originalColor || logoBase64 !== originalLogoBase64,
        [primaryColor, originalColor, logoBase64, originalLogoBase64]
    );

    const hasUnsavedChanges = unsavedTextChanges || unsavedBrandingChanges;

    const languagesToAdd = HOTEL_LANGUAGES.filter(lang => !availableLanguages.includes(lang.id));

    // Filtrar textos basado en búsqueda
    const filteredTexts = useMemo(() => {
        if (!searchText) return customTexts;
        const search = searchText.toLowerCase();
        return customTexts.filter(text => 
            text.key.toLowerCase().includes(search) || 
            text.value.toLowerCase().includes(search)
        );
    }, [customTexts, searchText]);

    // === CARGA DE DATOS ===
    const loadData = async (lang?: string) => {
        if (!hotelId) return;
        
        if (!hotel) setLoading(true);
        
        try {
            const hotelRes = hotel ? { data: hotel } : await dataProvider.getOne('hotels', { id: hotelId });
            const currentHotel = hotel || hotelRes.data;
            if (!hotel) setHotel(currentHotel);

            const textsRes = await dataProvider.getCustomTexts(hotelId);
            const languageToLoad = lang || currentHotel.defaultLanguage || 'ES';
            setSelectedLanguage(languageToLoad);

            const textsByLang = textsRes.data?.textsByLanguage || {};
            const activeLangs = Object.keys(textsByLang);
            if (!activeLangs.includes(languageToLoad)) {
                activeLangs.push(languageToLoad);
            }
            setAvailableLanguages(activeLangs.sort());

            const textsObject = textsByLang[languageToLoad] || {};
            const textsArray = Object.entries(textsObject).map(([key, value]) => ({ 
                key, 
                value: String(value) 
            }));
            
            setCustomTexts(textsArray);
            setOriginalTexts(textsArray);

            const brandingRes = await dataProvider.getBrandingConfiguration(hotelId);
            const brandingData = brandingRes.data || {};
            
            setPrimaryColor(brandingData.primaryColor || '#F85100');
            setOriginalColor(brandingData.primaryColor || '#F85100');
            setLogoBase64(brandingData.logoBase64 || null);
            setOriginalLogoBase64(brandingData.logoBase64 || null);

        } catch (error) {
            console.error('Error loading data:', error);
            notify('Error al cargar la configuración', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hotelId) {
            loadData();
        }
    }, [hotelId]);

    // === HANDLERS ===
    const handleTextValueChange = (index: number, value: string) => {
        const actualIndex = customTexts.findIndex(t => t.key === filteredTexts[index].key);
        setCustomTexts(prev => prev.map((item, i) => i === actualIndex ? { ...item, value } : item));
    };

    const handleDeleteText = (index: number) => {
        const keyToDelete = filteredTexts[index].key;
        setCustomTexts(prev => prev.filter(t => t.key !== keyToDelete));
    };

    const handleAddText = () => {
        const key = newText.key.trim();
        if (!key) {
            notify('La clave del texto no puede estar vacía', { type: 'warning' });
            return;
        }
        if (customTexts.some(t => t.key === key)) {
            notify('Esta clave ya existe', { type: 'warning' });
            return;
        }
        setCustomTexts(prev => [...prev, { key, value: newText.value.trim() }]);
        setNewText({ key: '', value: '' });
        setAddTextOpen(false);
        setSearchText('');
    };

    const handleAddLanguage = () => {
        if (!newLanguageCode) return;
        setAvailableLanguages(prev => [...prev, newLanguageCode].sort());
        setSelectedLanguage(newLanguageCode);
        setCustomTexts([]);
        setOriginalTexts([]);
        setAddLanguageOpen(false);
        setNewLanguageCode('');
        notify(`Idioma ${newLanguageCode.toUpperCase()} agregado`, { type: 'info' });
    };

    const handleLanguageChange = (event: React.SyntheticEvent, newLanguage: string) => {
        if (newLanguage && newLanguage !== selectedLanguage) {
            if (unsavedTextChanges) {
                setPendingLanguage(newLanguage);
                setChangeLanguageDialogOpen(true);
            } else {
                setSelectedLanguage(newLanguage);
                loadData(newLanguage);
                setSearchText('');
            }
        }
    };


const handleEnsureDefaultTexts = async (languageCode: string = selectedLanguage) => {
  if (!hotelId) return;
  
  setCheckingDefaultTexts(true);
  try {
    const result = await dataProvider.ensureDefaultTexts(hotelId, languageCode);
    
    if (result.data.textsAdded) {
      notify(translate('resources.hotels.messages.default_texts_added', 
        `Textos por defecto añadidos para ${languageCode}`), 
        { type: 'success' }
      );
      // Recargar los datos
      await loadData(languageCode);
    } else {
      notify(translate('resources.hotels.messages.texts_already_exist', 
        `Ya existen textos configurados para ${languageCode}`), 
        { type: 'info' }
      );
    }
  } catch (error) {
    console.error('Error ensuring default texts:', error);
    notify(translate('resources.hotels.errors.ensure_texts_failed', 
      'Error al verificar textos por defecto'), 
      { type: 'error' }
    );
  } finally {
    setCheckingDefaultTexts(false);
  }
};


    const handleLanguageChangeConfirm = () => {
        if (pendingLanguage) {
            setSelectedLanguage(pendingLanguage);
            loadData(pendingLanguage);
            setSearchText('');
        }
        setChangeLanguageDialogOpen(false);
        setPendingLanguage(null);
    };

    const handleSaveTexts = async () => {
        if (!hotelId || !permissions.canEdit) return;
        setSavingTexts(true);
        try {
            const textsObject = customTexts.reduce((acc, { key, value }) => {
                acc[key] = value;
                return acc;
            }, {} as Record<string, string>);
            await dataProvider.updateCustomTexts(hotelId, { [selectedLanguage]: textsObject });
            setOriginalTexts([...customTexts]);
            notify('Textos guardados con éxito', { type: 'success' });
        } catch (error) {
            notify('Error al guardar los textos', { type: 'error' });
        } finally {
            setSavingTexts(false);
        }
    };

    const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                notify('Por favor selecciona un archivo de imagen', { type: 'warning' });
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                notify('El archivo no debe superar los 2MB', { type: 'warning' });
                return;
            }
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setLogoBase64(e.target?.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        setLogoBase64(null);
        setLogoFile(null);
    };

    const handleSaveBranding = async () => {
        if (!hotelId || !permissions.canEdit) return;
        setSavingBranding(true);
        try {
            await dataProvider.updateBrandingConfiguration(hotelId, primaryColor, logoFile);
            setOriginalColor(primaryColor);
            setOriginalLogoBase64(logoBase64);
            setLogoFile(null);
            notify('Configuración de marca guardada con éxito', { type: 'success' });
        } catch (error) {
            notify('Error al guardar la configuración de marca', { type: 'error' });
        } finally {
            setSavingBranding(false);
        }
    };

    const handleBack = () => {
        if (hasUnsavedChanges) {
            setExitDialogOpen(true);
        } else {
            navigate(`/hotels/${hotelId}/edit`);
        }
    };

    const handleExitConfirm = () => {
        setExitDialogOpen(false);
        navigate(`/hotels/${hotelId}/edit`);
    };

    // === RENDERIZADO ===
    if (loading && !hotel) {
        return (
            <MyFormContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </MyFormContainer>
        );
    }

    if (!hotel) {
        return (
            <MyFormContainer>
                <Alert severity="error">No se pudo cargar la información del hotel</Alert>
            </MyFormContainer>
        );
    }

    return (
        <MyFormContainer fullWidth={true}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* HEADER con botón de regreso e indicador de cambios */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ color: 'text.secondary' }}
                    >
                        {translate('resources.hotels.actions.back_to_config', 'Volver a configuración')}
                    </Button>
                    {hasUnsavedChanges && (
                        <Chip
                            label={translate('resources.hotels.messages.unsaved_changes', 'Cambios sin guardar')}
                            color="warning"
                            size="small"
                            icon={<WarningIcon />}
                        />
                    )}
                </Box>

                {/* === SECCIÓN DE IDENTIDAD VISUAL (BRANDING) === */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '100%', maxWidth: 600 }}>
                        <MyFormSection
                            variant="outlined"
                            title={translate('resources.hotels.sections.branding', 'Identidad Visual')}
                            subtitle={translate('resources.hotels.sections.branding_subtitle', 'Personaliza el logo y color principal del hotel')}
                            icon={<PaletteIcon />}
                        >
                            <Stack 
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={4}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                justifyContent="center"
                                sx={{ py: 2 }}
                            >
                        {/* Logo */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                                {translate('resources.hotels.fields.logo', 'Logo del Hotel')}
                            </Typography>
                            {logoBase64 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                    <Avatar 
                                        src={logoBase64} 
                                        variant="rounded" 
                                        sx={{ 
                                            width: { xs: 120, sm: 150 },
                                            height: { xs: 80, sm: 100 },
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '& img': { objectFit: 'contain' } 
                                        }}
                                    />
                                    {permissions.canEdit && (
                                        <Stack direction="row" spacing={1}>
                                            <Button 
                                                component="label" 
                                                variant="outlined" 
                                                size="small" 
                                                startIcon={<UploadIcon />}
                                            >
                                                {translate('resources.hotels.actions.change_logo', 'Cambiar')}
                                                <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                color="error" 
                                                size="small" 
                                                startIcon={<DeleteIcon />} 
                                                onClick={handleRemoveLogo}
                                            >
                                                {translate('resources.hotels.actions.remove', 'Eliminar')}
                                            </Button>
                                        </Stack>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box 
                                        sx={{ 
                                            width: { xs: 120, sm: 150 },
                                            height: { xs: 80, sm: 100 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '2px dashed',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            bgcolor: 'action.hover',
                                            mb: 2
                                        }}
                                    >
                                        <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                                    </Box>
                                    {permissions.canEdit && (
                                        <Button 
                                            component="label" 
                                            variant="contained" 
                                            size="small"
                                            startIcon={<UploadIcon />}
                                        >
                                            {translate('resources.hotels.actions.upload_logo', 'Subir Logo')}
                                            <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>

                        <Divider 
                            orientation="vertical" 
                            flexItem 
                            sx={{ display: { xs: 'none', md: 'block' } }} 
                        />
                        <Divider 
                            sx={{ display: { xs: 'block', md: 'none' }, my: 2 }} 
                        />

                        {/* Color */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                                {translate('resources.hotels.fields.primary_color', 'Color Primario')}
                            </Typography>
                            <ColorPicker
                                value={primaryColor}
                                onChange={setPrimaryColor}
                                disabled={!permissions.canEdit}
                            />
                        </Box>
                    </Stack>

                    {permissions.canEdit && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    startIcon={savingBranding ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    onClick={handleSaveBranding}
                                    disabled={savingBranding || !unsavedBrandingChanges}
                                >
                                    {savingBranding 
                                        ? translate('resources.hotels.actions.saving', 'Guardando...') 
                                        : translate('resources.hotels.actions.save_branding', 'Guardar Identidad')}
                                </Button>
                            </Box>
                        </>
                    )}
                </MyFormSection>
                    </Box>
                </Box>


                {/* === SECCIÓN DE TEXTOS PERSONALIZADOS === */}
                <MyFormSection
                    variant="outlined"
                    title={translate('resources.hotels.sections.custom_texts', 'Textos Personalizados')}
                    subtitle={translate('resources.hotels.sections.custom_texts_subtitle', 'Personaliza los textos de la interfaz en múltiples idiomas')}
                    icon={<TextFieldsIcon />}
                >
                    {customTexts.length === 0 && permissions.canEdit && (
                        <Alert 
                        severity="info" 
                        sx={{ mb: 3 }}
                        action={
                            <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEnsureDefaultTexts(selectedLanguage)} // <-- CAMBIO AQUÍ
                            disabled={checkingDefaultTexts}
                            startIcon={checkingDefaultTexts ? 
                                <CircularProgress size={16} /> : 
                                <AddIcon />
                            }
                            >
                            {checkingDefaultTexts 
                                ? translate('resources.hotels.actions.checking', 'Verificando...') 
                                : translate('resources.hotels.actions.add_default_texts', 'Añadir textos por defecto')}
                            </Button>
                        }
                        >
                       
                        </Alert>
                    )}

                    {/* Controles superiores */}
                    <Box sx={{ mb: 3 }}>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            sx={{ mb: 2 }}
                        >
                            <ToggleButtonGroup 
                                value={selectedLanguage} 
                                exclusive 
                                onChange={handleLanguageChange} 
                                size="small"
                                sx={{ flexShrink: 0 }}
                            >
                                {availableLanguages.map((lang) => (
                                    <ToggleButton key={lang} value={lang} sx={{ minWidth: 50 }}>
                                        {lang}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            
                            {permissions.canEdit && (
                                <Button 
                                    size="small" 
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddLanguageOpen(true)}
                                    disabled={languagesToAdd.length === 0}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {translate('resources.hotels.actions.add_language', 'Añadir Idioma')}
                                </Button>
                            )}

                            <TextField
                                size="small"
                                fullWidth
                                placeholder={translate('resources.hotels.placeholders.search_texts', 'Buscar textos...')}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{ maxWidth: { sm: 300 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchText && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchText('')}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {permissions.canEdit && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddTextOpen(true)}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {translate('resources.hotels.actions.add_text', 'Añadir Texto')}
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Grid de textos en 3 columnas RESPONSIVE */}
                    {filteredTexts.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                {searchText 
                                    ? translate('resources.hotels.empty.no_results', { searchText })
                                    : translate('resources.hotels.empty.no_texts', 'No hay textos configurados para este idioma')}
                            </Typography>
                            {customTexts.length === 0 && permissions.canEdit && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddTextOpen(true)}
                                    sx={{ mt: 2 }}
                                >
                                    {translate('resources.hotels.actions.add_first_text', 'Añadir primer texto')}
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredTexts.map((text, index) => (
                                <Grid 
                                    item 
                                    xs={12}       // 1 columna en móvil
                                    sm={6}        // 2 columnas en tablet
                                    md={6}        // 2 columnas en desktop pequeño
                                    lg={4}        // 3 columnas en desktop grande
                                    key={text.key}
                                >
                                    <Box sx={{ 
                                        position: 'relative',
                                        '&:hover .delete-button': {
                                            opacity: 1
                                        }
                                    }}>
                                        <TextField
                                            fullWidth
                                            label={text.key}
                                            value={text.value}
                                            onChange={(e) => handleTextValueChange(index, e.target.value)}
                                            disabled={!permissions.canEdit}
                                            multiline
                                            minRows={2}
                                            maxRows={4}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                '& .MuiInputLabel-root': {
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500
                                                }
                                            }}
                                        />
                                        {permissions.canDelete && (
                                            <Tooltip title={translate('resources.hotels.actions.delete_text', 'Eliminar texto')}>
                                                <IconButton
                                                    className="delete-button"
                                                    size="small"
                                                    onClick={() => handleDeleteText(index)}
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 4,
                                                        top: 4,
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        bgcolor: 'background.paper',
                                                        boxShadow: 1,
                                                        '&:hover': {
                                                            bgcolor: 'error.lighter',
                                                            color: 'error.main'
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {/* Footer con información y botón guardar */}
                    {permissions.canEdit && customTexts.length > 0 && (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: 2,
                            mt: 3,
                            pt: 3,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary">
                                {searchText 
                                    ? translate('resources.hotels.info.showing_filtered', { 
                                        filteredCount: filteredTexts.length, 
                                        totalCount: customTexts.length 
                                    })
                                    : translate('resources.hotels.info.total_texts', { count: customTexts.length })}
                            </Typography>
                            <Button
                                variant="contained"
                             
                                startIcon={savingTexts ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSaveTexts}
                                disabled={savingTexts || !unsavedTextChanges}
                            >
                                {savingTexts 
                                    ? translate('resources.hotels.actions.saving', 'Guardando...') 
                                    : translate('resources.hotels.actions.save_texts', 'Guardar textos')}
                            </Button>
                        </Box>
                    )}
                </MyFormSection>
            </Box>

            {/* === DIÁLOGOS === */}
            <Dialog open={addLanguageOpen} onClose={() => setAddLanguageOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{translate('resources.hotels.dialogs.add_language_title', 'Añadir Nuevo Idioma')}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>{translate('resources.hotels.fields.language', 'Idioma')}</InputLabel>
                        <Select 
                            value={newLanguageCode} 
                            onChange={(e) => setNewLanguageCode(e.target.value)} 
                            label={translate('resources.hotels.fields.language', 'Idioma')}
                        >
                            {languagesToAdd.map(lang => (
                                <MenuItem key={lang.id} value={lang.id}>{lang.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddLanguageOpen(false)}>
                        {translate('ra.action.cancel', 'Cancelar')}
                    </Button>
                    <Button onClick={handleAddLanguage} variant="contained" disabled={!newLanguageCode}>
                        {translate('ra.action.add', 'Añadir')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addTextOpen} onClose={() => setAddTextOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{translate('resources.hotels.dialogs.add_text_title', 'Añadir Nueva Clave de Texto')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                fullWidth
                                label={translate('resources.hotels.fields.text_key', 'Clave')}
                                value={newText.key}
                                onChange={e => setNewText(prev => ({ 
                                    ...prev, 
                                    key: e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, '') 
                                }))}
                                helperText={translate('resources.hotels.help.text_key', 'Formato: categoria.nombre_texto (ej: button.submit)')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={translate('resources.hotels.fields.text_value', 'Valor')}
                                value={newText.value}
                                onChange={e => setNewText(prev => ({ ...prev, value: e.target.value }))}
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddTextOpen(false)}>
                        {translate('ra.action.cancel', 'Cancelar')}
                    </Button>
                    <Button onClick={handleAddText} variant="contained" disabled={!newText.key}>
                        {translate('ra.action.add', 'Añadir')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={exitDialogOpen} onClose={() => setExitDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: 'warning.main' }} />
                    {translate('resources.hotels.dialogs.unsaved_changes_title', 'Cambios sin guardar')}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {translate('resources.hotels.dialogs.unsaved_changes_warning', 'Los cambios realizados se perderán si sales sin guardar')}
                    </Alert>
                    <DialogContentText>
                        {unsavedTextChanges && unsavedBrandingChanges 
                            ? translate('resources.hotels.dialogs.unsaved_both', 'Tienes cambios sin guardar tanto en los textos como en la identidad visual.')
                            : unsavedTextChanges 
                            ? translate('resources.hotels.dialogs.unsaved_texts', 'Tienes cambios sin guardar en los textos personalizados.')
                            : translate('resources.hotels.dialogs.unsaved_branding', 'Tienes cambios sin guardar en la identidad visual.')}
                        {' '}{translate('resources.hotels.dialogs.confirm_exit', '¿Estás seguro de que deseas salir?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, gap: 1 }}>
                    <Button onClick={() => setExitDialogOpen(false)} variant="outlined">
                        {translate('resources.hotels.actions.continue_editing', 'Continuar editando')}
                    </Button>
                    <Button onClick={handleExitConfirm} variant="contained" color="warning">
                        {translate('resources.hotels.actions.exit_without_saving', 'Salir sin guardar')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={changeLanguageDialogOpen} onClose={() => setChangeLanguageDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TranslateIcon sx={{ color: 'primary.main' }} />
                    {translate('resources.hotels.dialogs.change_language_title', 'Cambiar idioma')}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {translate('resources.hotels.dialogs.unsaved_language_warning', 'Tienes cambios sin guardar en el idioma actual')}
                    </Alert>
                    <DialogContentText>
                        {translate('resources.hotels.dialogs.change_language_message', { selectedLanguage })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, gap: 1 }}>
                    <Button onClick={() => setChangeLanguageDialogOpen(false)} variant="outlined">
                        {translate('ra.action.cancel', 'Cancelar')}
                    </Button>
                    <Button onClick={handleLanguageChangeConfirm} variant="contained" color="primary">
                        {translate('resources.hotels.actions.change_language', 'Cambiar idioma')}
                    </Button>
                </DialogActions>
            </Dialog>
        </MyFormContainer>
    );
};

export default HotelTexts;